import { createSelector, createSlice } from '@reduxjs/toolkit';

// Dedicated slice for the "Companies > Approved" screen.
// NOTE: this is intentionally separate from the thin `company.js` slice, which only
// feeds entity dropdowns for shift creation. This slice owns the paginated, filterable
// approved-companies list (mirrors the `talent` slice shape).
const approvedCompaniesSlice = createSlice({
    name: 'approvedCompanies',
    initialState: {
        filters: [],
        companies: [],
        totalRecords: 0,
        isLoading: false,
    },
    reducers: {
        fetchCompanies: state => {
            state.companies = [];
            state.isLoading = true;
        },
        companiesFetched: (state, { payload }) => {
            const { results, totalRecords } = payload;
            state.companies = results;
            state.totalRecords = totalRecords;
            state.isLoading = false;
        },
        companiesFetchRequestFailed: state => {
            state.isLoading = false;
        },
        // Removes a row locally (e.g. after Delete Company) without a refetch.
        removeCompany: (state, { payload }) => {
            state.companies = state.companies.filter(c => c._id !== payload.id);
        },
        // Shallow-merges updated fields into a single row (e.g. after Edit Profile).
        companyPartialUpdate: (state, { payload }) => {
            const { userId, updatedData } = payload;
            state.companies = state.companies.map(company =>
                company._id === userId
                    ? { ...company, ...updatedData }
                    : company,
            );
        },
        // Same filter-array contract as the talent slice.
        upsertFilter: (state, { payload }) => {
            const { key, value } = payload;
            const index = state.filters.findIndex(f => f.key === key);

            if (index === -1 && value === '') {
                return;
            }

            if (index === -1) {
                state.filters.push(payload);
            } else {
                state.filters[index].value = value;
            }
        },
        removeFilter: (state, { payload }) => {
            const index = state.filters.findIndex(f => f.key === payload);
            if (index !== -1) {
                state.filters.splice(index, 1);
            }
        },
        removeAllFilters: state => {
            state.filters = [];
        },
    },
});

// SELECTORS
const getSelf = state => state.approvedCompanies;

export const getCompanies = createSelector(getSelf, state => state.companies);
export const getFilters = createSelector(getSelf, state => state.filters);
export const isLoading = createSelector(getSelf, state => state.isLoading);
export const getTotalRecords = createSelector(
    getSelf,
    state => state.totalRecords,
);

export const {
    fetchCompanies,
    companiesFetched,
    companiesFetchRequestFailed,
    removeCompany,
    companyPartialUpdate,
    upsertFilter,
    removeFilter,
    removeAllFilters,
} = approvedCompaniesSlice.actions;

export default approvedCompaniesSlice.reducer;
